import { Model } from '@/models/Model';
import {
  AddOptions,
  CacheStrategy,
  UpdateItem,
  UpdateOptions,
} from './strategy/CacheStrategy';

export interface CacheCommandI<T> {
  execute(): void;
  undo?(): void;
  strategy: CacheStrategy<T>;
}

export abstract class CacheCommand<T> implements CacheCommandI<T> {
  private previousState: T | T[] | undefined;
  public strategy: CacheStrategy<T>;
  constructor(strategy: CacheStrategy<T>) {
    this.strategy = strategy;
    this.previousState = strategy.get();
  }

  execute() {
    this.previousState = this.strategy.get();
  }

  undo(): void {
    if (this.previousState) {
      this.strategy.set(this.previousState);
    }
  }
}

export class AddCommand<T extends Model> extends CacheCommand<T> {
  constructor(
    strategy: CacheStrategy<T>,
    private data: T,
    private options?: AddOptions,
  ) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.add?.(this.data, this.options);
  }
}

export class UpdateCommand<T extends Model> extends CacheCommand<T> {
  constructor(
    strategy: CacheStrategy<T>,
    private data: UpdateItem<T>,
    private options?: UpdateOptions,
  ) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.update?.(this.data, this.options);
  }
}

export class RemoveCommand<T extends Model> extends CacheCommand<T> {
  constructor(strategy: CacheStrategy<T>, private id: number) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.remove(this.id);
  }
}

export class SetCommand<T extends Model> extends CacheCommand<T> {
  constructor(strategy: CacheStrategy<T>, private data: T | T[]) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.set(this.data);
  }
}

export class MergeCommand<T extends Model> extends CacheCommand<T> {
  constructor(strategy: CacheStrategy<T>, private data: Partial<T>) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.merge(this.data);
  }
}
